import { SplitFlapBoard } from './split-flap.js';
import { WebSocketClient } from './websocket-client.js';
import type { BoardState } from '@flipboard/shared';

interface PlaylistData {
  id: string;
  name: string | null;
  rows: number | null;
  cols: number | null;
  fillScreen: boolean | null;
  soundEnabled: boolean | null;
  defaultDuration: number;
  shuffle: boolean;
  showWatermark: boolean;
  items: PlaylistItem[];
}

interface PlaylistItem {
  id: string;
  playlistId: string;
  boardId: string;
  position: number;
  duration: number | null;
  board?: {
    id: string;
    name: string | null;
    rows: number;
    cols: number;
    showWatermark?: boolean;
  };
}

export class PlaylistController {
  private playlistId: string;
  private playlist: PlaylistData | null = null;
  private items: PlaylistItem[] = [];
  private currentIndex: number = 0;
  private wsClients: Map<string, WebSocketClient> = new Map();
  private boardStates: Map<string, BoardState> = new Map();
  private board: SplitFlapBoard;
  private canvas: HTMLCanvasElement;
  private isAnimating: boolean = false;
  private isInitialLoad: boolean = true;
  private timer: number | null = null;
  private pollInterval: number | null = null;
  private apiBase: string;
  private onConnectionChange: (connected: boolean) => void;
  private onError: (error: string) => void;

  constructor(
    playlistId: string,
    canvas: HTMLCanvasElement,
    apiBase: string,
    onConnectionChange: (connected: boolean) => void,
    onError: (error: string) => void
  ) {
    this.playlistId = playlistId;
    this.canvas = canvas;
    this.apiBase = apiBase;
    this.onConnectionChange = onConnectionChange;
    this.onError = onError;

    // Create board instance (will be configured after fetching playlist)
    this.board = new SplitFlapBoard(canvas, {
      rows: 6,
      cols: 22,
      speed: 1.0,
      soundEnabled: false,
      volume: 0.3,
      frameEnabled: true,
      showWatermark: true,
      onAnimationComplete: () => this.notifyAnimationComplete(),
    });
  }

  async initialize(): Promise<void> {
    try {
      // Fetch playlist data
      const response = await fetch(`${this.apiBase}/api/playlists/${this.playlistId}`);
      if (!response.ok) {
        throw new Error(`Failed to fetch playlist: ${response.statusText}`);
      }

      const data = await response.json();
      this.playlist = data.playlist;
      this.items = data.playlist.items || [];

      if (this.items.length === 0) {
        this.onError('This playlist is empty');
        return;
      }

      // Sort items by position
      this.items.sort((a, b) => a.position - b.position);

      // Apply shuffle if enabled
      if (this.playlist.shuffle) {
        this.shuffleItems();
      }

      // Configure board with playlist settings (or first board's settings)
      const firstBoard = this.items[0].board;
      const rows = this.playlist.rows !== null ? Number(this.playlist.rows) : (firstBoard?.rows ?? 6);
      const cols = this.playlist.cols !== null ? Number(this.playlist.cols) : (firstBoard?.cols ?? 22);
      const fillScreen = this.playlist.fillScreen ?? false;
      const soundEnabled = this.playlist.soundEnabled ?? false;
      const showWatermark = this.playlist.showWatermark;

      console.log(`[Playlist] Initial config - rows: ${rows}, cols: ${cols}, fillScreen: ${fillScreen}, sound: ${soundEnabled}`);
      console.log(`[Playlist] Playlist overrides - rows: ${this.playlist.rows}, cols: ${this.playlist.cols}`);

      this.board.setBoard(rows, cols);
      this.board.frameEnabled = !fillScreen;
      this.board.soundEnabled = soundEnabled;
      this.board.showWatermark = showWatermark;

      // Connect WebSocket to all boards
      this.connectToAllBoards();

      // Start with first board (index 0)
      this.currentIndex = 0;
      // Note: Initial animation will be triggered when first state arrives via WebSocket

      // Start polling for playlist updates every 30 seconds
      this.startPolling();
    } catch (error) {
      console.error('Failed to initialize playlist:', error);
      this.onError(`Failed to load playlist: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  private startPolling(): void {
    // Poll every 30 seconds for playlist updates
    this.pollInterval = window.setInterval(() => {
      this.checkForUpdates();
    }, 30000);
  }

  private async checkForUpdates(): Promise<void> {
    try {
      const response = await fetch(`${this.apiBase}/api/playlists/${this.playlistId}`);
      if (!response.ok) return;

      const data = await response.json();
      const newPlaylist = data.playlist;
      const newItems = (newPlaylist.items || []).sort((a: PlaylistItem, b: PlaylistItem) => a.position - b.position);

      // Check if items changed (added, removed, or reordered)
      const itemsChanged = this.hasItemsChanged(newItems);

      if (itemsChanged) {
        console.log('[Playlist] Detected playlist changes, updating...');

        // Update playlist settings
        this.playlist = newPlaylist;
        this.items = newItems;

        // Apply shuffle if enabled
        if (this.playlist.shuffle) {
          this.shuffleItems();
        }

        // Connect to any new boards
        this.connectToAllBoards();

        // Disconnect from boards no longer in playlist
        const currentBoardIds = new Set(this.items.map(item => item.boardId));
        this.wsClients.forEach((client, boardId) => {
          if (!currentBoardIds.has(boardId)) {
            console.log(`[Playlist] Disconnecting from removed board ${boardId}`);
            client.disconnect();
            this.wsClients.delete(boardId);
            this.boardStates.delete(boardId);
          }
        });

        // If current board was removed, move to first board
        if (this.currentIndex >= this.items.length) {
          console.log('[Playlist] Current board was removed, moving to first board');
          this.currentIndex = 0;
          this.transitionToBoard(this.currentIndex);
        }
      }
    } catch (error) {
      console.error('[Playlist] Failed to check for updates:', error);
    }
  }

  private hasItemsChanged(newItems: PlaylistItem[]): boolean {
    if (newItems.length !== this.items.length) {
      return true;
    }

    for (let i = 0; i < newItems.length; i++) {
      if (newItems[i].boardId !== this.items[i].boardId ||
          newItems[i].duration !== this.items[i].duration ||
          newItems[i].position !== this.items[i].position) {
        return true;
      }
    }

    return false;
  }

  private shuffleItems(): void {
    // Fisher-Yates shuffle
    for (let i = this.items.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [this.items[i], this.items[j]] = [this.items[j], this.items[i]];
    }
  }

  private connectToAllBoards(): void {
    this.items.forEach((item) => {
      if (this.wsClients.has(item.boardId)) return;

      const wsClient = new WebSocketClient({
        boardId: item.boardId,
        onState: (state: BoardState) => {
          console.log(`[Playlist] Received state for board ${item.boardId}`);

          // If playlist has size overrides, apply them to the state
          if (this.playlist?.rows !== null && this.playlist?.cols !== null) {
            state = {
              ...state,
              rows: Number(this.playlist.rows),
              cols: Number(this.playlist.cols),
            };
            console.log(`[Playlist] Overriding state dimensions to playlist size: ${state.rows}×${state.cols}`);
          }

          this.boardStates.set(item.boardId, state);

          // If this is the current board
          if (this.items[this.currentIndex]?.boardId === item.boardId) {
            if (this.isInitialLoad) {
              // On initial load, animate the first state in
              console.log('[Playlist] Initial state received, animating in first board');
              this.isAnimating = true;
              this.board.applyStateAnimated(state);
              this.handleResize();
            } else {
              // On subsequent updates, apply state instantly
              this.board.applyState(state);
            }
          }
        },
        onMessage: (text: string, clear?: boolean) => {
          // Only apply if this is the current board
          if (this.items[this.currentIndex]?.boardId === item.boardId) {
            this.board.applyMessage(text, clear);
          }
        },
        onClear: () => {
          if (this.items[this.currentIndex]?.boardId === item.boardId) {
            this.board.clear();
          }
        },
        onResize: (rows: number, cols: number) => {
          // Update board dimensions in cache
          if (item.board) {
            item.board.rows = rows;
            item.board.cols = cols;
          }

          // Apply if current board and no playlist size override
          if (
            this.items[this.currentIndex]?.boardId === item.boardId &&
            this.playlist?.rows === null &&
            this.playlist?.cols === null
          ) {
            this.board.setBoard(rows, cols);
            this.handleResize();
          }
        },
        onSound: (enabled: boolean) => {
          // Apply if current board and no playlist sound override
          if (
            this.items[this.currentIndex]?.boardId === item.boardId &&
            this.playlist?.soundEnabled === null
          ) {
            this.board.soundEnabled = enabled;
            if (enabled && this.board.audioContext?.state === 'suspended') {
              this.board.audioContext.resume();
            }
          }
        },
        onFillScreen: (enabled: boolean) => {
          // Apply if current board and no playlist fillScreen override
          if (
            this.items[this.currentIndex]?.boardId === item.boardId &&
            this.playlist?.fillScreen === null
          ) {
            this.board.frameEnabled = !enabled;
            this.handleResize();
          }
        },
        onConnectionChange: (connected: boolean) => {
          // Report connection status for current board
          if (this.items[this.currentIndex]?.boardId === item.boardId) {
            this.onConnectionChange(connected);
          }
        },
        onError: (error: string) => {
          console.error(`[Playlist] Error for board ${item.boardId}:`, error);
        },
      });

      wsClient.connect();
      this.wsClients.set(item.boardId, wsClient);
    });
  }

  private transitionToBoard(index: number, isFirstLoad: boolean = false): void {
    if (index < 0 || index >= this.items.length) return;

    const item = this.items[index];
    console.log(`[Playlist] Transitioning to board ${item.boardId} (${index + 1}/${this.items.length})`);

    // Clear any existing timer
    if (this.timer !== null) {
      clearTimeout(this.timer);
      this.timer = null;
    }

    // Apply playlist size overrides if set, otherwise use board's size
    let rows: number;
    let cols: number;

    if (this.playlist?.rows !== null && this.playlist?.cols !== null) {
      rows = Number(this.playlist.rows);
      cols = Number(this.playlist.cols);
      console.log(`[Playlist] Using playlist size override: ${rows}×${cols}`);
    } else if (item.board) {
      rows = item.board.rows;
      cols = item.board.cols;
      console.log(`[Playlist] Using board size: ${rows}×${cols}`);
    } else {
      rows = 6;
      cols = 22;
      console.log(`[Playlist] Using default size: ${rows}×${cols}`);
    }

    // Only resize if dimensions actually changed
    if (this.board.rows !== rows || this.board.cols !== cols) {
      console.log(`[Playlist] Resizing board from ${this.board.rows}×${this.board.cols} to ${rows}×${cols}`);
      this.board.setBoard(rows, cols);
    }

    // Apply cached state if available
    let state = this.boardStates.get(item.boardId);
    if (state) {
      // If playlist has size overrides, ensure state matches those dimensions
      if (this.playlist?.rows !== null && this.playlist?.cols !== null) {
        state = {
          ...state,
          rows: rows,
          cols: cols,
        };
      }

      if (isFirstLoad) {
        this.isAnimating = true;
        this.board.applyStateAnimated(state);
        // Animation complete callback will be called by board
      } else {
        this.isAnimating = true;
        this.board.applyStateAnimated(state);
      }
    } else {
      // No cached state, just schedule next transition
      this.scheduleNextTransition();
    }

    this.handleResize();
  }

  // Called by SplitFlapBoard when animation completes
  notifyAnimationComplete(): void {
    console.log('[Playlist] Animation complete, scheduling next transition');
    this.isAnimating = false;

    // Mark initial load as complete after first animation
    if (this.isInitialLoad) {
      this.isInitialLoad = false;
      console.log('[Playlist] Initial load animation complete');
    }

    this.scheduleNextTransition();
  }

  private scheduleNextTransition(): void {
    // Don't schedule if still animating
    if (this.isAnimating) {
      console.log('[Playlist] Still animating, not scheduling yet');
      return;
    }

    const item = this.items[this.currentIndex];
    const duration = item.duration ?? this.playlist?.defaultDuration ?? 10;
    const durationMs = duration * 1000;

    console.log(`[Playlist] Scheduling next transition in ${duration}s`);

    this.timer = window.setTimeout(() => {
      this.transitionToNext();
    }, durationMs);
  }

  private transitionToNext(): void {
    this.currentIndex = (this.currentIndex + 1) % this.items.length;
    this.transitionToBoard(this.currentIndex);
  }

  private handleResize(): void {
    const fillScreen = this.playlist?.fillScreen ?? false;
    const maxWidth = fillScreen ? window.innerWidth : window.innerWidth * 0.95;
    const maxHeight = fillScreen ? window.innerHeight : window.innerHeight * 0.9;
    this.board.fitToSize(maxWidth, maxHeight);
  }

  public resize(): void {
    this.handleResize();
  }

  public destroy(): void {
    // Clear timer
    if (this.timer !== null) {
      clearTimeout(this.timer);
      this.timer = null;
    }

    // Clear polling interval
    if (this.pollInterval !== null) {
      clearInterval(this.pollInterval);
      this.pollInterval = null;
    }

    // Disconnect all WebSocket clients
    this.wsClients.forEach((client) => client.disconnect());
    this.wsClients.clear();

    // Destroy board
    this.board.destroy();
  }
}
